/**
 * Mirrors the `filters:` block of `winsight-server`'s `reports/t2-business-dashboard/report.yaml`,
 * which is authoritative.
 *
 * The optional dimensions are declared exactly as `t2-business` declares them, and draw their
 * values from the same view, so the dashboard and the grid cannot offer different values for the
 * same filter.
 *
 * `measure` and `order_state` are answer-defining, not presentation: they decide what every panel
 * counts, and the server ranks, folds and shares against them. They are filters in the URL, not
 * local toggles (§4.4).
 */

import { format, startOfYear } from 'date-fns';

import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

/** Option values come from `report.yaml`'s `options:` block, never a hardcoded array (§5.4). */
const live = liveOptionsFor('t2-business-dashboard');

const ISO = 'yyyy-MM-dd';

/**
 * Opens on the year to date — the window the dashboard is read in most often, and the one the
 * design opens on. The reader can widen it; the fact view itself starts at 2023, before which the
 * FX rates the value measure depends on do not exist.
 */
export function defaultPeriod(today: Date = new Date()): { from: string; to: string } {
  return { from: format(startOfYear(today), ISO), to: format(today, ISO) };
}

/**
 * Money against quantity. A governed pair rather than a list read from the data — no column
 * enumerates them. Value leads because it is the default.
 */
const MEASURE_OPTIONS: readonly FilterOption[] = [
  { value: 'value', label: 'Value (HKD)' },
  { value: 'volume', label: 'Volume' },
];

/**
 * Which lines a measure counts. "Received" and "Open" split on whether a line has a delivery yet —
 * the same test SDP uses — so the two add back to "Ordered" exactly.
 */
const ORDER_STATE_OPTIONS: readonly FilterOption[] = [
  { value: 'ordered', label: 'Total ordered' },
  { value: 'received', label: 'Received' },
  { value: 'open', label: 'Open orders' },
];

const INTERLINING_OPTIONS: readonly FilterOption[] = [
  { value: 'true', label: 'Included' },
  { value: 'false', label: 'Excluded' },
];

export const DEFAULT_COMPANY_CODE = 'H018';

export function t2BusinessDashboardFilters(): FilterSchema {
  return [
    {
      type: 'select',
      name: 'buyer',
      label: 'Company Code',
      required: true,
      options: live('buyer'),
      defaultValue: DEFAULT_COMPANY_CODE,
    },
    {
      type: 'date-range',
      name: 'period',
      label: 'Order date',
      required: true,
      fromParam: 'from',
      toParam: 'to',
      defaultValue: defaultPeriod,
    },
    {
      type: 'select',
      name: 'order_state',
      label: 'Order state',
      required: true,
      options: ORDER_STATE_OPTIONS,
      defaultValue: 'ordered',
    },
    // Fabric against trims. The users' spreadsheet splits every table on this, so it leads the
    // optional filters. Named the way the grid names it (§4.10).
    {
      type: 'select',
      name: 'material_class',
      label: 'Material Type',
      anyLabel: 'Fabric & Acc.',
      options: live('material_class'),
    },
    // Normalised on the server: picking SS26 also brings in the S26 and SP26 spellings of the same
    // buy, and the list offers only the canonical SS/FW codes (report.yaml).
    {
      type: 'multi-select',
      name: 'season',
      label: 'Season',
      anyLabel: 'All seasons',
      options: live('season'),
    },
    {
      type: 'multi-select',
      name: 't2_group',
      label: 'Supplier group',
      anyLabel: 'All suppliers',
      options: live('t2_group'),
    },
    {
      type: 'multi-select',
      name: 'factory',
      label: 'Factory',
      anyLabel: 'All factories',
      options: live('factory'),
    },
    {
      type: 'select',
      name: 'measure',
      label: 'Measure',
      required: true,
      options: MEASURE_OPTIONS,
      presentation: 'segmented',
      // Opens on value: the dashboard is read as a spend book first. Value carries the source's
      // unit-price/UOM defect, so the block's footer states the caveat whenever it is the active
      // measure — which, on the default, is straight away rather than only once someone switches.
      defaultValue: 'value',
    },
    {
      type: 'select',
      name: 'include_interlining',
      label: 'Include Interlining?',
      required: true,
      options: INTERLINING_OPTIONS,
      presentation: 'segmented',
      defaultValue: 'true',
    },
  ];
}
